import React from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IndianRupee, Trash2, Plus, AlertCircle } from "lucide-react";
import { YearWiseFee } from "@/services/studentService";
import { formatCurrency } from "@/lib/model";

export type YearWiseDiscount = {
  year: number;
  amount: string;
  reason: string;
};

interface YearWiseDiscountInputProps {
  yearWiseFees: YearWiseFee[];
  discounts: YearWiseDiscount[];
  onChange: (discounts: YearWiseDiscount[]) => void;
  disabled?: boolean;
}

const YearWiseDiscountInput: React.FC<YearWiseDiscountInputProps> = ({
  yearWiseFees,
  discounts,
  onChange,
  disabled = false,
}) => {
  const handleAddDiscount = (year: number) => {
    const existing = discounts.find((d) => d.year === year);
    if (existing) return; // Already has discount for this year

    onChange([...discounts, { year, amount: "0", reason: "" }]);
  };

  const handleRemoveDiscount = (year: number) => {
    onChange(discounts.filter((d) => d.year !== year));
  };

  const handleUpdateDiscount = (
    year: number,
    field: "amount" | "reason",
    value: string
  ) => {
    onChange(
      discounts.map((d) =>
        d.year === year ? { ...d, [field]: value } : d
      )
    );
  };

  const getTotalDiscount = () => {
    return discounts.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
  };

  const getTotalFee = () => {
    return yearWiseFees.reduce((sum, fee) => sum + fee.feeAmount, 0);
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <IndianRupee className="h-4 w-4" />
          Year-wise Discount Management
        </h4>
        <p className="text-xs text-blue-700 mb-3">
          Add discounts for specific years. Each year can have its own discount
          amount and reason.
        </p>

        {/* Year-wise Fee List with Discount Options */}
        <div className="space-y-3">
          {yearWiseFees.map((fee) => {
            const discount = discounts.find((d) => d.year === fee.yearNumber);
            const hasDiscount = !!discount;

            return (
              <motion.div
                key={fee.yearNumber}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`border rounded-lg p-3 ${
                  hasDiscount
                    ? "bg-orange-50 border-orange-200"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={hasDiscount ? "default" : "outline"}>
                      Year {fee.yearNumber}
                    </Badge>
                    <span className="text-sm font-medium text-gray-700">
                      Fee: {formatCurrency(fee.feeAmount)}
                    </span>
                  </div>
                  {!disabled && (
                    <Button
                      type="button"
                      size="sm"
                      variant={hasDiscount ? "destructive" : "outline"}
                      onClick={() =>
                        hasDiscount
                          ? handleRemoveDiscount(fee.yearNumber)
                          : handleAddDiscount(fee.yearNumber)
                      }
                    >
                      {hasDiscount ? (
                        <>
                          <Trash2 className="h-3 w-3 mr-1" />
                          Remove
                        </>
                      ) : (
                        <>
                          <Plus className="h-3 w-3 mr-1" />
                          Add Discount
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {hasDiscount && discount && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-2 mt-3"
                  >
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">
                        Discount Amount (₹) <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                        value={discount.amount}
                        onChange={(e) =>
                          handleUpdateDiscount(
                            fee.yearNumber,
                            "amount",
                            e.target.value
                          )
                        }
                        placeholder="0"
                        min="0"
                        max={fee.feeAmount}
                        disabled={disabled}
                        className="h-8"
                      />
                      <p className="text-xs text-gray-500">
                        Maximum: {formatCurrency(fee.feeAmount)}
                      </p>
                      {parseFloat(discount.amount) > fee.feeAmount && (
                        <div className="flex items-center gap-1 text-xs text-red-600">
                          <AlertCircle className="h-3 w-3" />
                          Discount exceeds year fee
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">
                        Reason <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={discount.reason}
                        onChange={(e) =>
                          handleUpdateDiscount(
                            fee.yearNumber,
                            "reason",
                            e.target.value
                          )
                        }
                        placeholder="Merit scholarship, financial aid, etc."
                        disabled={disabled}
                        className="h-8"
                      />
                    </div>
                    <div className="text-sm font-medium text-orange-700 flex items-center justify-between border-t pt-2">
                      <span>Final Amount for Year {fee.yearNumber}:</span>
                      <span>
                        {formatCurrency(
                          Math.max(
                            0,
                            fee.feeAmount - (parseFloat(discount.amount) || 0)
                          )
                        )}
                      </span>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-4 pt-4 border-t border-blue-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="text-center">
              <p className="text-xs text-gray-600 font-medium">Total Course Fee</p>
              <p className="text-lg font-bold text-green-600">
                {formatCurrency(getTotalFee())}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-600 font-medium">Total Discount</p>
              <p className="text-lg font-bold text-orange-600">
                -{formatCurrency(getTotalDiscount())}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-600 font-medium">Final Amount</p>
              <p className="text-lg font-bold text-blue-600">
                {formatCurrency(getTotalFee() - getTotalDiscount())}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YearWiseDiscountInput;
